// Utility functions for opening hours

const dayNames = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday'
};

const dayLabels = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche'
};

export const getDayKey = (date = new Date()) => {
  return dayNames[date.getDay()];
};

export const getDayLabel = (dayKey) => {
  return dayLabels[dayKey] || dayKey;
};

export const getAllDays = () => {
  return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
};

/**
 * Check if a restaurant is currently open
 * @param {Object} openingHours - The opening_hours object from restaurant
 * @returns {Object} { isOpen: boolean, opensAt: string|null, closesAt: string|null }
 */
export const checkIsOpen = (openingHours) => {
  if (!openingHours) {
    return { isOpen: true, opensAt: null, closesAt: null, message: null };
  }

  const now = new Date();
  const todayKey = getDayKey(now);
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const todayHours = openingHours[todayKey];

  // No hours set for today = closed
  if (!todayHours) {
    return { isOpen: false, opensAt: null, closesAt: null, message: 'Fermé aujourd\'hui' };
  }

  // Format time helper
  const formatTime = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  // New format with slots: { is_open: bool, slots: [{ open, close }] }
  if (todayHours.slots !== undefined) {
    if (todayHours.is_open === false) {
      return { isOpen: false, opensAt: null, closesAt: null, message: 'Fermé aujourd\'hui' };
    }
    const slots = todayHours.slots || [];
    if (slots.length === 0) {
      return { isOpen: true, opensAt: null, closesAt: null, message: null };
    }
    for (const slot of slots) {
      if (slot.open && slot.close) {
        const [oH, oM] = slot.open.split(':').map(Number);
        const [cH, cM] = slot.close.split(':').map(Number);
        const openMin = oH * 60 + oM;
        const closeMin = cH * 60 + cM;
        if (currentMinutes >= openMin && currentMinutes < closeMin) {
          return { isOpen: true, opensAt: slot.open, closesAt: slot.close, message: `Ferme à ${slot.close}` };
        }
      }
    }
    // Not in any slot - find next opening
    const nextSlot = slots.find(s => {
      if (!s.open) return false;
      const [oH, oM] = s.open.split(':').map(Number);
      return (oH * 60 + oM) > currentMinutes;
    });
    if (nextSlot) {
      return { isOpen: false, opensAt: nextSlot.open, closesAt: nextSlot.close, message: `Ouvre à ${nextSlot.open}` };
    }
    return { isOpen: false, opensAt: null, closesAt: null, message: 'Fermé' };
  }

  // Old object format: { open: "11:00", close: "22:00" }
  let openTime, closeTime;
  
  if (typeof todayHours === 'object' && todayHours.open && todayHours.close) {
    const [openH, openM] = todayHours.open.split(':').map(Number);
    const [closeH, closeM] = todayHours.close.split(':').map(Number);
    openTime = openH * 60 + openM;
    closeTime = closeH * 60 + closeM;
  } 
  // Handle string format: "11:00-22:00"
  else if (typeof todayHours === 'string') {
    if (todayHours.toLowerCase() === 'fermé' || todayHours.toLowerCase() === 'closed') {
      return { isOpen: false, opensAt: null, closesAt: null, message: 'Fermé aujourd\'hui' };
    }
    const match = todayHours.match(/(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})/);
    if (!match) {
      return { isOpen: true, opensAt: null, closesAt: null, message: null };
    }
    openTime = parseInt(match[1]) * 60 + parseInt(match[2]);
    closeTime = parseInt(match[3]) * 60 + parseInt(match[4]);
  } else {
    return { isOpen: true, opensAt: null, closesAt: null, message: null };
  }

  const isOpen = currentMinutes >= openTime && currentMinutes < closeTime;

  if (isOpen) {
    const closesAt = formatTime(closeTime);
    return { 
      isOpen: true, 
      opensAt: formatTime(openTime),
      closesAt,
      message: `Ferme à ${closesAt}`
    };
  } else {
    if (currentMinutes < openTime) {
      const opensAt = formatTime(openTime);
      return { 
        isOpen: false, 
        opensAt,
        closesAt: formatTime(closeTime),
        message: `Ouvre à ${opensAt}`
      };
    } else {
      return { 
        isOpen: false, 
        opensAt: formatTime(openTime),
        closesAt: formatTime(closeTime),
        message: 'Fermé'
      };
    }
  }
};

/**
 * Format opening hours for a specific day
 * @param {Object|string} hours - Hours for a day
 * @returns {string} Formatted hours string
 */
export const formatDayHours = (hours) => {
  if (!hours) return 'Fermé';
  
  // New format with slots: { is_open: bool, slots: [{ open, close }] }
  if (typeof hours === 'object' && hours.slots !== undefined) {
    if (hours.is_open === false) return 'Fermé';
    const slots = hours.slots || [];
    if (slots.length === 0) return 'Ouvert';
    return slots
      .filter(s => s.open && s.close)
      .map(s => `${s.open} - ${s.close}`)
      .join(', ') || 'Fermé';
  }
  
  // Old format: { open: "11:00", close: "22:00" }
  if (typeof hours === 'object' && hours.open && hours.close) {
    return `${hours.open} - ${hours.close}`;
  }
  
  // String format
  if (typeof hours === 'string') {
    if (hours.toLowerCase() === 'fermé' || hours.toLowerCase() === 'closed') {
      return 'Fermé';
    }
    return hours;
  }
  
  return 'Fermé';
};

/**
 * Get formatted opening hours for all days
 * @param {Object} openingHours - The opening_hours object
 * @returns {Array} Array of { day, dayLabel, hours, isToday }
 */
export const getFormattedSchedule = (openingHours) => {
  const todayKey = getDayKey();
  const days = getAllDays();
  
  return days.map(day => ({
    day,
    dayLabel: getDayLabel(day),
    hours: formatDayHours(openingHours?.[day]),
    isToday: day === todayKey
  }));
};
